import React, { useState, useEffect, useContext } from "react";
import { UserDataContext } from "../App";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery, Typography, Button } from "@mui/material";
import { Logo, Eng, Rus, Profile, Logout, CloseIcon } from "../assets/images/icons";
import Auth from "./forms/Auth";
import ForgotPass from "./forms/ForgotPass";
import Register from "./forms/Register";
import ChangePass from "./forms/ChangePass";
import RegisterApprove from "./forms/RegisterApprove";
import { styled, useTheme } from "@mui/material/styles";
import { Link, NavLink } from "react-router-dom";
import REST from '../libs/API';
import { getText, trimString } from '../libs/utils';
import { useNavigate } from 'react-router-dom';
import LangDropdown from "./LangDropdown";

const MyHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '32px 0 55px',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    padding: '27px 0 24px',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: '17px 0 24px',
  },
  [theme.breakpoints.only('xs')]: {
    padding: '17px 0 32px',
  }
}))

const UserBox = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.6)',
  borderRadius: 28,
  fontSize: 14,
  height: 'fit-content',
  fontWeight: 600,
  display: 'flex',
  margin: '0 25px 0 0',
  [theme.breakpoints.only('xs')]: {
    margin: '0',
  },
  padding: '0 11px 0 0',
  alignItems: 'center'
}));

const RoundIcon = styled(Box)(() => ({
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.6)',
  marginLeft: 8,
  "&.disabled": {
    filter: 'grayscale(100%)'
  }
}))

export default function Header({ lang }) {
  const navigate = useNavigate();
  const userData = useContext(UserDataContext);
  const theme = useTheme();
  let isXS = useMediaQuery(theme.breakpoints.only('xs'));
  const [openDialog, setOpenDialog] = useState(false);
  const [activeForm, setActiveForm] = useState('Register');
  const [socialLinks, setSocialLinks] = useState(null)
  const formsProps = { setActiveForm, setOpenDialog, socialLinks };
  const forms = {
    Auth: { title: getText('loginHeader'), content: <Auth {...formsProps} /> },
    AuthNoRedirect: { title: getText('loginHeader'), content: <Auth {...formsProps} noRedirect={true} /> },
    ForgotPass: { title: getText('recoveryH1'), content: <ForgotPass {...formsProps} /> },
    Register: { title: getText('registerH1'), content: <Register {...formsProps} /> },
    RegisterApprove: { title: getText('headRegisterConfirm'), content: <RegisterApprove {...formsProps} /> },
    ChangePass: { title: getText('accChangePassFieldNew'), content: <ChangePass {...formsProps} resetToken={userData.resetToken} /> },
  }

  const loadUrlData = () => {
    const qParams = new URLSearchParams(window.location.search);

    if (qParams.get("resetToken")) {
      setActiveForm('ChangePass');
      setOpenDialog(true);
    }
  }

  const openForm = (form) => {
    setActiveForm(form);
    setOpenDialog(true);
  }

  const handlerChangeLang = (attr) => {
    if (localStorage.lang !== attr) {
      if (userData.token) {
        REST.setProfileLang(userData.token);
      }
      localStorage.lang = attr;
      window.location.reload();
    }
  }

  const handlerDeAuth = () => {
    window.showMessage(getText("loggedOut"));
    userData.deAuth();
  }

  const catchSocAuthToken = (data) => {
    if (data.data.success) {
      userData.auth(data.data.token, data.data.email);
      navigate('/account');
      setOpenDialog(false);
    } else {
      window.showMessage('Ошибка авторизации');
    }
  };


  useEffect(() => {
    loadUrlData();
    window.openHeaderForm = openForm;
    REST.getSocAuthLinks().then((data) => {
      window.catchSocAuthToken = catchSocAuthToken;
      if (data.success) {
        setSocialLinks(data.data);
      }
    });
  }, []);

  return (
    <MyHeader>
      <Link to="/"><Logo /></Link>
      <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'space-between', width: { sm: 'auto', xs: '100%' }, margin: { sm: 0, xs: '10px 0 0' } }}>
        <Button onClick={() => navigate('/rules/aml')} sx={{
          marginRight: { xs: '16px', sm: '40px' },
          "&:active": {
            background: '#FFE7E7',
            color: '#E43C3C',
          },
          height: '40px'
        }}>AML/KYC</Button>
        {userData.siteStatus == 10 && <>
          {userData.token
            ? <UserBox>
              <Link to="/account">
                <RoundIcon style={{ margin: 0 }}><Profile /></RoundIcon>
              </Link>
              <Box style={{ padding: "0 12px 0 10px" }}>
                <Link to="/account">
                  <Typography fontWeight={600} color={'primary.main'} sx={{
                    wordBreak: 'keep-all',
                    lineBreak: 'strict'
                  }}>
                    {isXS && userData?.email?.length > 10 ? userData.email.slice(0, 9) + '...' : userData.email}
                  </Typography>
                </Link>
              </Box>
              <Box style={{ cursor: "pointer", padding: '4px 0 0' }} onClick={handlerDeAuth}><Logout /></Box>
            </UserBox>
            : <Box sx={{
              order: { xs: -1, sm: 0 },
              flex: { xs: 1, sm: 0 },
            }}>
              <RoundIcon style={{ margin: '0 25px 0 0' }} onClick={() => openForm('Auth')}>
                <Profile />
              </RoundIcon>
            </Box>
          }
        </>}
        {isXS && <LangDropdown>
          <RoundIcon className={lang !== 'ru' ? 'disabled' : ''} onClick={() => handlerChangeLang('ru')}><Rus /></RoundIcon>
          <RoundIcon className={lang !== 'en' ? 'disabled' : ''} onClick={() => handlerChangeLang('en')}><Eng /></RoundIcon>
        </LangDropdown>}

        {!isXS && <Box sx={{ display: 'flex' }}>
          <RoundIcon className={lang !== 'ru' ? 'disabled' : ''} onClick={() => handlerChangeLang('ru')}><Rus /></RoundIcon>
          <RoundIcon className={lang !== 'en' ? 'disabled' : ''} onClick={() => handlerChangeLang('en')}><Eng /></RoundIcon>
        </Box>}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        className="userForm"
      >
        <DialogTitle variant="h4" component="h4" align="center">
          {forms[activeForm].title}
          <CloseIcon
            onClick={() => setOpenDialog(false)}
            style={{
              position: "absolute",
              right: 12,
              top: 10,
              cursor: 'pointer'
            }}
          />
        </DialogTitle>
        <DialogContent>
          {forms[activeForm].content}
        </DialogContent>
      </Dialog>
    </MyHeader>
  );
}
